<template>
  <form class="sign-up-form w-100">
    <div class="input-group-lg">
      <div
        class="d-flex justify-content-center"
        v-if="isLoading && fulfill_sync_details?.allowed">
        <font-awesome-icon
          :icon="['fa', 'circle-notch']"
          class="fa-spin m-auto spinner fa-4x egx-partner-color"
        />
      </div>
      <ValidationObserver
        tag="form"
        @submit.prevent="updateUser()"
        ref="userEdit"
        v-else
      >
        <div v-if="isFulFillSynced">
          <div class="form-group row form">
            <label class="col-12 col-md-4 col-form-label d-flex field-label align-items-center">
              User Full Name
            </label>

            <div class="col-12 col-md-8 user-name-col">
              <div class="form-field-container">
                <span
                  id="user-name"
                  class="form-control input-text input-field readonly-pill mb-0 w-100"
                >
                  {{ currentUser.name }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <form-field-template
          :formFields="fields"
          :show="true"
          :model="data.updateUser"
          :id-prefix="idPrefix"
          :isFulFillSyncAllowed="fulfill_sync_details?.allowed"
          :isUserEditPage="true"
        />

        <div class="form-check col-12" v-if="allowConsentField">
          <input
            class="form-check-input" type="checkbox"
            v-model="data.updateUser.consent"
            :class="settings.consent.class"
            id="consent"
            :disabled="disable_edit_profile && !detailVerificationStep"
          >
          <label
            class="form-check-label"
            for="consent"
            v-safe-html="settings.consent.html_content"
          />
        </div>

        <div class="form-check col-12" v-if="allowMarketingField">
          <input
            class="form-check-input" type="checkbox"
            v-model="data.updateUser.subscribed"
            :class="settings.marketing.class"
            id="user-subscribed"
            :disabled="disable_edit_profile && !detailVerificationStep"
          >
          <label class="form-check-label" for="user-subscribed">
            {{ settings.marketing.text }}
          </label>
        </div>

        <button
          type="submit"
          class="btn btn-lg d-flex ml-auto mr-auto mt-4"
          :class="settings.attributes.button3.class"
          v-if="detailVerificationStep"
        >
          {{ settings.attributes.button3.text }}
        </button>

        <button
          type="submit"
          class="btn btn-lg d-flex ml-auto mr-auto mt-4"
          :class="settings.attributes.button.class"
          v-else-if="!disable_edit_profile || currentUser.extra_info.educate_created_sync"
        >
          {{ settings.attributes.button.text }}
        </button>

        <button
          type="button"
          class="btn btn-lg d-flex ml-auto mr-auto mt-4"
          :class="settings.attributes.button2.class"
          @click="redirectBack(settings.attributes.button2['path-name'])"
          v-else
        >
          {{ settings.attributes.button2.text }}
        </button>
      </ValidationObserver>
    </div>
  </form>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import FormFieldTemplate from './form/FormFieldTemplate.vue';
import router from '../router/index';

export default {
  name: 'UserFormFields',

  components: {
    FormFieldTemplate,
    ValidationObserver,
  },

  props: {
    isFulFillSync: Boolean,
    detailVerificationStep: Boolean,
    conditionalDisplay: Boolean,
    conditionalDisplay2: Boolean,
  },

  data() {
    return {
      idPrefix: 'user-edit-page',
      showSuccessAlert: false,
      showErrorAlert: false,
      errorAlertMsg: '',
      sitesFields: [],
      data: {
        updateUser: {

        },
      },
      isLoading: false,
      windowWidth: window.innerWidth,
    };
  },

  created() {
    window.addEventListener('resize', this.updateWindowWidth);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.updateWindowWidth);
  },

  mounted() {
    if (this.fulfill_sync_details && this.fulfill_sync_details.allowed) {
      this.getUpdatedUser();
      this.getSiteFields();
    } else {
      this.updateUserData();
    }
  },

  computed: {
    partner() {
      return this.$store.state.partners.currentPartner;
    },

    currentUser() {
      return this.$store.state.users.currentUser;
    },

    settings() {
      return this.partner.settings.sections.user_edit_page;
    },
    fulfill_sync_details() {
      return this.partner.settings.fulfill_sync_details;
    },
    disable_edit_profile() {
      return this.partner.settings.disable_user_profile_edition || this.isFulFillSync;
    },

    fields() {
      let fields = this.settings.fields;
      if (this.disable_edit_profile && !this.detailVerificationStep && !this.currentUser.extra_info.educate_created_sync) {
        fields = fields.map((field) => ({
          ...field,
          disabled: 'true',
        }));
      }

      fields = fields.filter((field) => (
        (!field.conditional_display || (this.conditionalDisplay === true && field.conditional_display === true)) &&
        (!field.conditional_display2 || (this.conditionalDisplay2 === true && field.conditional_display2 === true))
      ));

      if (this.isFulFillSynced) {
        fields = fields.filter((field) => (
          field.name !== 'first_name' && field.name !== 'last_name'
        ));
      }

      return fields;
    },

    allowConsentField() {
      return !this.data.updateUser.consent &&
        this.settings.consent && this.settings.consent?.show !== false &&
          this.settings.consent?.html_content;
    },

    allowMarketingField() {
      return this.settings.marketing && this.settings.marketing?.show !== false;
    },

    isFulFillSynced() {
      return this.fulfill_sync_details?.allowed && (this.currentUser.fulfill_account_id || '').length > 1;
    }
  },

  methods: {
    updateUser() {
      let userdata = this.data.updateUser;
      if (this.disable_edit_profile && !this.detailVerificationStep && !this.currentUser.extra_info.educate_created_sync) {
        userdata = {
          subscribed: this.data.updateUser.subscribed,
          extra_info: this.data.updateUser.extra_info,
        };
      }

      if (this.allowConsentField) {
        this.$notify({
          group: 'notice-stay',
          type: 'error',
          text: 'You must agree to the terms and conditions to proceed.',
        });
      } else {
        this.$refs.userEdit.validate()
          .then((isValid) => {
            if (isValid) {
              this.$emit('loadingScreen', true);
              this.$store.dispatch('users/put', userdata)
                .then(() => {
                  if (this.detailVerificationStep) {
                    router.push({ name: 'fetch-account-details', query: { isSearchDetail: true } });
                  } else {

                    this.$store.dispatch('users/get').then(() => {
                      this.$emit('update-success');
                    });
                  }
                })
                .catch((errors) => {
                  let message = errors?.response?.data?.errors?.[0]?.title;
                  if (message) {
                    message = `Reason: ${message}`
                  }

                  console.log(message);
                  this.$notify({
                    group: 'notice-stay',
                    type: 'error',
                    text: `Unable to proceed. Please try again. ${message}`,
                  });
                }).finally(() => {
                  this.$emit('loadingScreen', false);
                });

            } else {
              this.$notify({
                group: 'notice-stay',
                type: 'error',
                text: 'Fill in all required fields.',
              });
            }
          });
      }
    },

    updateUserData() {
      this.data.updateUser = {
        ...this.$store.state.users.currentUser,
        ...this.$store.state.users.currentUser.extra_info,
      };

      if (this.fulfill_sync_details?.allowed && ['new', 'user'].includes(this.data.updateUser.first_name.toLowerCase())) {
        this.data.updateUser.name = '';
        this.data.updateUser.first_name = '';
        this.data.updateUser.last_name = '';
      }
    },

    getSiteFields() {
      return this.$store.dispatch('users/fetchSiteFields').then((response) => {
        this.sitesFields = response.data;
      })
    },
    redirectBack(name) {
      router.push({ name });
    },

    getUpdatedUser() {
      this.isLoading = true;
      this.$store.dispatch('users/get').then(() => {
        this.updateUserData();
      }).catch((error) => {
        console.error('Error fetching user data:', error);
      }).finally(() => {
        this.isLoading = false;
      });
    },

    updateWindowWidth() {
      this.windowWidth = window.innerWidth;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../assets/stylesheets/pages/user_edit.scss';
.form-field-container {
  width: 100%;
  min-width: 0;
}

.readonly-pill {
  display: block;
  width: 100%;
  min-height: 41px;
  padding: 0.375rem 0.75rem;
  font-size: 1.25rem;
  line-height: 1.5;
  color: #495057;
  background-color: #e9ecef;
  border: 1px solid #ced4da;
  border-radius: 0;
  appearance: none;

  white-space: normal;
  overflow-wrap: anywhere;
}

.user-name-col {
  display: flex;
  align-items: stretch;
  padding-left: 0.25rem;
}

@media (max-width: 768px) {
  .user-name-col {
    padding-left: 12px;
  }
  .form-group .col-form-label {
    margin-bottom: .25rem;
  }
}
</style>
